import type { FC, SetStateAction } from "react";
import React from "react";

import HelpSquare from "@/assets/icons/help-square";
import DialogCustom from "@/components/modals/dialog-custom";
import { Separator } from "@/components/ui/separator";
import type { ToothBrushHairType } from "@/types/api/patient";
import type { Perioquiz } from "@/types/api/perioquiz";
import { getFeelingLabels, getToothBrushType } from "@/utils/patient";

interface PerioquizSideModalProps {
  perioquiz: Perioquiz | null;
  open: boolean;
  onOpenChange: React.Dispatch<SetStateAction<boolean>>;
}

interface PerioquizQuestionProps {
  step: number;
  question: string;
  value: string;
}

const getToothbrushHairTypeLabel = (
  toothbrushHairType: ToothBrushHairType
): string => {
  switch (toothbrushHairType) {
    case "SOFT":
      return "Souples";
    case "MEDIUM":
      return "Moyens";
    case "HARD":
      return "Durs";
  }
};

const PerioquizQuestion: FC<PerioquizQuestionProps> = ({
  step,
  question,
  value,
}) => {
  return (
    <div className="flex flex-col gap-2">
      <div className="text-xs text-gray-600">{`Question ${step} sur 12`}</div>
      <div className="flex items-baseline gap-4">
        <div className="font-medium whitespace-nowrap">{question}</div>
        <div className="text-lg text-brand-700">{value}</div>
      </div>
    </div>
  );
};

const PerioquizSideModal: FC<PerioquizSideModalProps> = ({
  perioquiz,
  open,
  onOpenChange,
}) => {
  if (!perioquiz) {
    return null;
  }

  const questions: { [key: string]: { question: string; value: string } } = {
    feeling: {
      question: "Ressentez-vous :",
      value:
        perioquiz.feeling.length > 0
          ? getFeelingLabels(perioquiz.feeling).join(", ")
          : "Non renseigné",
    },
    bleed: {
      question: "Saignez-vous des gencives ?",
      value:
        perioquiz.bleed !== null
          ? perioquiz.bleed
            ? "Oui"
            : "Non"
          : "Non renseigné",
    },
    spontaneousBleeding: {
      question: "Vous saignez",
      value:
        perioquiz.spontaneousBleed !== null
          ? perioquiz.spontaneousBleed
            ? "Spontanément"
            : "Après le brossage"
          : "Non renseigné",
    },
    brushingFrequency: {
      question: "À quelle fréquence vous brossez-vous les dents ?",
      value: perioquiz.brushingFrequency
        ? perioquiz.brushingFrequency
        : "Non renseigné",
    },
    toothbrushType: {
      question: "Quel est votre type de brosse à dents ?",
      value:
        perioquiz.toothbrushType !== null
          ? getToothBrushType(perioquiz.toothbrushType)
          : "Non renseigné",
    },
    toothbrushHairType: {
      question: "Les poils sont-ils :",
      value:
        perioquiz.toothbrushHairType !== null
          ? getToothbrushHairTypeLabel(perioquiz.toothbrushHairType)
          : "Non renseigné",
    },
    wire: {
      question: "Utilisez-vous du fil dentaire ?",
      value:
        perioquiz.wire !== null
          ? perioquiz.wire
            ? "Oui"
            : "Non"
          : "Non renseigné",
    },
    mouthwash: {
      question: "Utilisez-vous des bains de bouche ?",
      value:
        perioquiz.mouthwash !== null
          ? perioquiz.mouthwash
            ? "Oui"
            : "Non"
          : "Non renseigné",
    },
    tongue: {
      question: "Utilisez-vous un gratte langue ?",
      value:
        perioquiz.tongue !== null
          ? perioquiz.tongue
            ? "Oui"
            : "Non"
          : "Non renseigné",
    },
    brush: {
      question: "Utilisez-vous des brossettes ?",
      value:
        perioquiz.brush !== null
          ? perioquiz.brush
            ? "Oui"
            : "Non"
          : "Non renseigné",
    },
    other: {
      question: "Utilisez-vous autre chose ?",
      value: perioquiz.other ?? "Non renseigné",
    },
    toothpaste: {
      question: "Quelle est la marque de votre dentifrice ?",
      value:
        perioquiz.toothpaste !== null ? perioquiz.toothpaste : "Non renseigné",
    },
  };

  return (
    <DialogCustom
      title="Réponses du patient au perioquiz"
      description={
        <div className="text-sm text-gray-600">{`Retrouvez les réponses du patient au Perioquiz du ${new Date(perioquiz.createdAt).toLocaleDateString()}`}</div>
      }
      open={open}
      onOpenChange={onOpenChange}
      side
      startIcon={
        <div className="flex p-3 w-12 h-12 justify-center items-center border border-gray-200 rounded-[10px] mr-4">
          <HelpSquare className="w-6 h-6" />
        </div>
      }
      className="max-w-[40%] pb-0"
    >
      <Separator />
      <div className="flex flex-col gap-4 p-8 bg-grey-25">
        <PerioquizQuestion
          step={1}
          question={questions.feeling.question}
          value={questions.feeling.value}
        />
        <PerioquizQuestion
          step={2}
          question={questions.bleed.question}
          value={questions.bleed.value}
        />
        {perioquiz.bleed ? (
          <PerioquizQuestion
            step={3}
            question={questions.spontaneousBleed.question}
            value={questions.spontaneousBleed.value}
          />
        ) : null}
        <PerioquizQuestion
          step={4}
          question={questions.brushingFrequency.question}
          value={questions.brushingFrequency.value}
        />
        <PerioquizQuestion
          step={5}
          question={questions.toothbrushType.question}
          value={questions.toothbrushType.value}
        />
        <PerioquizQuestion
          step={6}
          question={questions.toothbrushHairType.question}
          value={questions.toothbrushHairType.value}
        />
        <PerioquizQuestion
          step={7}
          question={questions.wire.question}
          value={questions.wire.value}
        />
        <PerioquizQuestion
          step={8}
          question={questions.mouthwash.question}
          value={questions.mouthwash.value}
        />
        <PerioquizQuestion
          step={9}
          question={questions.tongue.question}
          value={questions.tongue.value}
        />
        <PerioquizQuestion
          step={10}
          question={questions.brush.question}
          value={questions.brush.value}
        />
        <PerioquizQuestion
          step={11}
          question={questions.other.question}
          value={questions.other.value}
        />
        <PerioquizQuestion
          step={12}
          question={questions.toothpaste.question}
          value={questions.toothpaste.value}
        />
      </div>
    </DialogCustom>
  );
};

export default PerioquizSideModal;
